import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Loader2, ArrowRight, CheckCircle, SkipForward, 
  Clock, Sparkles, Crown, ChevronRight, Pencil,
  Target, Heart, History, Check
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { 
  getDashboardSummary,
  requestActionSuggestion, 
  recordActionResponse,
  getSubscriptionStatus
} from '../services/api';
import EmailGate from '../components/EmailGate';

// ==================================================
// PROFILE CONTEXT BLOCK - Always shows user's profile
// ==================================================

const ProfileContextBlock = ({ profileSummary, onEditProfile }) => {
  const { goal_text, values, capacity, is_complete, profile_exists } = profileSummary;
  
  if (!profile_exists) {
    return null;
  }
  
  return (
    <div className="space-y-4 mb-8">
      {/* Goal Card */}
      <div className="bg-fern-card rounded-xl p-5 border border-calm-border">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 mb-2">
            <Target className="h-4 w-4 text-forest" />
            <span className="text-sm font-medium text-calm-text">Your current goal</span>
          </div>
          <button 
            onClick={onEditProfile}
            className="text-calm-text-secondary hover:text-forest transition-colors p-1"
            title="Edit profile"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
        {goal_text ? (
          <p className="text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
            {goal_text}
          </p>
        ) : (
          <button
            onClick={onEditProfile}
            className="text-forest hover:text-forest-dark text-sm flex items-center gap-1"
          >
            Set a goal <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </div>
      
      {/* Values & Capacity Card */}
      <div className="bg-fern-card rounded-xl p-5 border border-calm-border">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Heart className="h-4 w-4 text-forest" />
            <span className="text-sm font-medium text-calm-text">Your values</span>
          </div>
        </div>
        
        {/* Value chips */}
        {values && values.length > 0 ? (
          <div className="flex flex-wrap gap-2 mb-4">
            {values.slice(0, 5).map((value, i) => (
              <span 
                key={i}
                className="px-3 py-1 bg-herbal-light/50 text-forest text-sm rounded-full border border-herbal/30"
              >
                {value}
              </span>
            ))}
            {values.length > 5 && (
              <span className="px-2 py-1 text-calm-text-secondary text-sm">
                +{values.length - 5} more
              </span>
            )}
          </div>
        ) : (
          <p className="text-calm-text-secondary text-sm mb-4">
            No values set yet
          </p>
        )}
        
        {/* Capacity row */}
        {capacity && (capacity.time || capacity.money || capacity.attention) && (
          <div className="flex flex-wrap gap-4 text-sm text-calm-text-secondary border-t border-calm-border/50 pt-3">
            {capacity.time && (
              <div className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                <span>{capacity.time}</span>
              </div>
            )}
            {capacity.money && (
              <span>{capacity.money}</span>
            )}
          </div>
        )}
        
        {!is_complete && (
          <button
            onClick={onEditProfile}
            className="text-forest hover:text-forest-dark text-sm flex items-center gap-1 mt-3"
          >
            Complete profile <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
};

// ==================================================
// ACTION CARD - Active suggestion display
// ==================================================

const ActionCard = ({ suggestion, onAccept, onSkip, isLoading }) => {
  if (!suggestion) return null;
  
  const { action, why_this_fits, category } = suggestion;
  
  const categoryColors = {
    learn: 'bg-blue-100 text-blue-700',
    help: 'bg-green-100 text-green-700',
    connect: 'bg-purple-100 text-purple-700',
    steward: 'bg-amber-100 text-amber-700',
    build: 'bg-indigo-100 text-indigo-700',
    reflect: 'bg-rose-100 text-rose-700'
  };
  
  return (
    <div className="bg-fern-card rounded-2xl p-6 border border-calm-border shadow-lg mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-forest" />
          <span className="text-sm font-medium text-calm-text">Suggested Action</span>
        </div>
        {category && (
          <span className={`px-2 py-1 text-xs rounded-full ${categoryColors[category] || 'bg-gray-100 text-gray-700'}`}>
            {category}
          </span>
        )}
      </div>
      
      {/* Title */}
      <h3 className="text-xl text-calm-text mb-3" style={{fontFamily: 'DM Serif Display, serif'}}>
        {action?.title}
      </h3>
      
      {/* Description */}
      <p className="text-calm-text-secondary mb-4">
        {action?.description}
      </p>
      
      {/* Effort & Impact */}
      <div className="flex flex-wrap gap-4 mb-4 text-sm">
        <div className="flex items-center gap-1 text-calm-text-secondary">
          <Clock className="h-4 w-4" />
          <span>{action?.effort}</span>
        </div>
        {action?.impact && (
          <div className="text-forest">
            {action?.impact}
          </div>
        )}
      </div>
      
      {/* Why this action */}
      <div className="bg-herbal-light/30 rounded-xl p-4 mb-6">
        <p className="text-sm font-medium text-calm-text mb-1">Why this action?</p>
        <p className="text-sm text-calm-text-secondary">
          {why_this_fits}
        </p>
      </div>
      
      {/* Steps */}
      {action?.steps && action.steps.length > 0 && (
        <div className="mb-6">
          <p className="text-sm font-medium text-calm-text mb-2">Steps:</p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-calm-text-secondary">
            {action.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>
      )}
      
      {/* Action buttons */}
      <div className="flex gap-3">
        <Button
          onClick={onAccept}
          disabled={isLoading}
          className="flex-1 bg-forest hover:bg-forest-dark text-white"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              Accept
            </>
          )}
        </Button>
        <Button
          onClick={onSkip}
          disabled={isLoading}
          variant="outline"
          className="flex-1 border-calm-border text-forest hover:bg-fern"
        >
          <SkipForward className="h-4 w-4 mr-2" />
          Skip
        </Button>
      </div>
      
      <p className="text-xs text-calm-text-secondary text-center mt-4">
        Skipping has no penalty. Silence is valid.
      </p>
    </div>
  );
};

// ==================================================
// CREATE PROFILE CARD - Shown when no profile exists
// ==================================================

const CreateProfileCard = ({ onCreateProfile }) => {
  return (
    <div className="bg-fern-card rounded-2xl p-8 border border-calm-border shadow-lg text-center mb-6">
      <div className="w-16 h-16 bg-herbal-light/50 rounded-full flex items-center justify-center mx-auto mb-4">
        <Heart className="h-8 w-8 text-forest" />
      </div>
      <h2 className="text-xl text-calm-text mb-3" style={{fontFamily: 'DM Serif Display, serif'}}>
        Let's start with your values
      </h2>
      <p className="text-calm-text-secondary mb-6 max-w-md mx-auto text-sm">
        Create your values profile to receive personalized suggestions aligned with what matters most to you.
      </p>
      
      <Button
        onClick={onCreateProfile}
        className="bg-forest hover:bg-forest-dark text-white px-8 py-5"
      >
        Create your profile
        <ArrowRight className="h-5 w-5 ml-2" />
      </Button>
      
      <p className="text-xs text-calm-text-secondary mt-4">
        Takes about 2 minutes
      </p>
    </div>
  );
};

// ==================================================
// READY CARD - Request new suggestion
// ==================================================

const ReadyCard = ({ onRequest, isRequesting, isProfileComplete, error }) => {
  return (
    <div className="bg-fern-card rounded-2xl p-8 border border-calm-border shadow-lg text-center mb-6">
      <Sparkles className="h-10 w-10 text-forest/40 mx-auto mb-4" />
      <h2 className="text-xl text-calm-text mb-3" style={{fontFamily: 'DM Serif Display, serif'}}>
        Ready for a suggestion?
      </h2>
      <p className="text-calm-text-secondary mb-6 max-w-md mx-auto text-sm">
        {isProfileComplete 
          ? 'When you\'re ready, request a personalized action aligned with your values and capacity.'
          : 'Request a starter suggestion, or complete your profile for more personal matching.'
        }
      </p>
      
      {error && (
        <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3 mb-4">
          {error}
        </div>
      )}
      
      <Button
        onClick={onRequest}
        disabled={isRequesting}
        className="bg-forest hover:bg-forest-dark text-white px-8 py-5"
      >
        {isRequesting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Finding suggestion...
          </>
        ) : (
          <>
            Request a suggestion
            <ArrowRight className="h-5 w-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

// ==================================================
// RECENT ACTION CARD
// ==================================================

const RecentActionCard = ({ recentAction }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  return (
    <div className="bg-fern-card rounded-xl p-5 border border-calm-border mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Check className="h-4 w-4 text-forest" />
        <span className="text-sm font-medium text-calm-text">Recent action</span>
      </div>
      
      {recentAction ? (
        <div>
          <p className="text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
            {recentAction.title}
          </p>
          {recentAction.summary && (
            <p className="text-sm text-calm-text-secondary mt-1">
              {recentAction.summary}
            </p>
          )}
          <p className="text-xs text-calm-text-secondary mt-2">
            Accepted {formatDate(recentAction.accepted_at)}
          </p>
        </div>
      ) : (
        <p className="text-sm text-calm-text-secondary">
          Nothing logged yet. Accepting an action will keep a gentle record here.
        </p>
      )}
    </div>
  );
};

// ==================================================
// HISTORY CARD
// ==================================================

const HistoryCard = ({ history }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  const statusBadge = (status) => {
    const badges = {
      accepted: 'bg-green-100 text-green-700',
      skipped: 'bg-gray-100 text-gray-600',
      offered: 'bg-blue-100 text-blue-600'
    };
    const labels = {
      accepted: 'Accepted',
      skipped: 'Skipped',
      offered: 'Offered'
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full ${badges[status] || badges.offered}`}>
        {labels[status] || 'Offered'}
      </span>
    );
  };
  
  return (
    <div className="bg-fern-card rounded-xl p-5 border border-calm-border mb-6">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-forest" />
        <span className="text-sm font-medium text-calm-text">History</span>
      </div>
      
      {history && history.length > 0 ? (
        <div className="space-y-3">
          {history.map((item, i) => (
            <div 
              key={item.suggestion_id || i}
              className="flex items-start justify-between py-2 border-b border-calm-border/50 last:border-0 last:pb-0"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm text-calm-text truncate pr-2">
                  {item.title}
                </p>
                <p className="text-xs text-calm-text-secondary mt-0.5">
                  {formatDate(item.generated_at)}
                </p>
              </div>
              {statusBadge(item.status)}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-calm-text-secondary">
          No history yet.
        </p>
      )}
    </div>
  );
};

// ==================================================
// CIRCLE PROMO
// ==================================================

const CirclePromo = ({ isCircleMember, onLearnMore }) => {
  if (isCircleMember) {
    // Circle member badge (minimal)
    return (
      <div className="bg-herbal-light/20 rounded-xl p-4 border border-herbal/30 flex items-center gap-3">
        <Crown className="h-5 w-5 text-forest" />
        <span className="text-sm text-forest font-medium">Circle member</span>
      </div>
    );
  }
  
  return (
    <div className="bg-herbal-light/20 rounded-xl p-5 border border-herbal/30">
      <div className="flex items-start gap-3">
        <Crown className="h-6 w-6 text-forest flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-base font-medium text-calm-text mb-1" style={{fontFamily: 'DM Serif Display, serif'}}>
            Join the Circle
          </h3>
          <p className="text-sm text-calm-text-secondary mb-3">
            Support OHMNAI and unlock long-term alignment memory and pooled actions.
          </p>
          <Button
            onClick={onLearnMore}
            variant="outline"
            className="border-forest text-forest hover:bg-fern text-sm py-2"
          >
            Learn more
            <ArrowRight className="h-3.5 w-3.5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================================================
// CONFIRMATION TOAST
// ==================================================

const ConfirmationToast = ({ message, show }) => {
  if (!show) return null;
  
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-forest text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 animate-fade-in z-50">
      <CheckCircle className="h-4 w-4" />
      <span className="text-sm">{message}</span>
    </div>
  );
};

// ==================================================
// MAIN DASHBOARD COMPONENT
// ==================================================

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  
  // State
  const [dashboardData, setDashboardData] = useState({
    profile_summary: {
      profile_exists: false,
      goal_text: null,
      values: [],
      capacity: {},
      is_complete: false
    },
    recent_action: null,
    history: []
  });
  const [suggestion, setSuggestion] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '' });
  
  // Load all dashboard data
  const loadDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load dashboard summary (profile, recent action, history)
      const summary = await getDashboardSummary();
      setDashboardData(summary);
      
      // Load subscription status for authenticated users
      if (isAuthenticated) {
        try {
          const subData = await getSubscriptionStatus();
          setSubscription(subData);
        } catch (e) {
          // Subscription might not exist - that's okay
        }
      }
    } catch (e) {
      console.error('Error loading dashboard:', e);
      // Don't show error - use fallback data
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);
  
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadDashboardData();
    } else if (!authLoading) {
      setIsLoading(false);
    }
  }, [authLoading, isAuthenticated, loadDashboardData]);
  
  // Show toast helper
  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: '' }), 2000);
  };
  
  // Request suggestion
  const handleRequestSuggestion = async () => {
    setIsRequesting(true);
    setError(null);
    try {
      const result = await requestActionSuggestion();
      setSuggestion({
        suggestion_id: result.suggestion?.suggestion_id,
        action: result.suggestion?.action,
        why_this_fits: result.suggestion?.why_this_fits,
        category: result.suggestion?.category,
        source: result.suggestion?.source
      });
    } catch (e) {
      console.error('Error requesting suggestion:', e);
      setError(e.message || 'Failed to get suggestion');
    } finally {
      setIsRequesting(false);
    }
  };
  
  // Accept suggestion
  const handleAccept = async () => {
    if (!suggestion?.suggestion_id) return;
    setIsResponding(true);
    try {
      await recordActionResponse(suggestion.suggestion_id, 'accepted');
      setSuggestion(null);
      showToast('Noted.');
      // Refresh dashboard data to update recent action and history
      await loadDashboardData();
    } catch (e) {
      console.error('Error recording response:', e);
    } finally {
      setIsResponding(false);
    }
  };
  
  // Skip suggestion
  const handleSkip = async () => {
    if (!suggestion?.suggestion_id) return;
    setIsResponding(true);
    try {
      await recordActionResponse(suggestion.suggestion_id, 'skipped');
      setSuggestion(null);
      showToast('Understood.');
      // Refresh dashboard data to update history
      await loadDashboardData();
    } catch (e) {
      console.error('Error recording response:', e);
    } finally {
      setIsResponding(false);
    }
  };
  
  const handleEditProfile = () => navigate('/values-profile');
  const handleCreateProfile = () => navigate('/values-profile');
  const handleCircleLearnMore = () => navigate('/circle');
  
  const isCircleMember = subscription?.status === 'active' || user?.circle_override;
  const isProfileComplete = dashboardData.profile_summary?.is_complete;
  const hasProfile = dashboardData.profile_summary?.profile_exists;
  
  // Show loading spinner during auth check
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }
  
  // Show EmailGate for non-authenticated users
  if (!isAuthenticated) {
    return <EmailGate next="/dashboard" />;
  }
  
  // Dashboard loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern">
      {/* Header */}
      <header className="border-b border-calm-border bg-fern-card/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="text-2xl font-bold text-calm-text cursor-pointer" 
              style={{fontFamily: 'DM Serif Display, serif'}}
              onClick={() => navigate('/')}
            >
              OHMNAI
            </div>
            {isCircleMember && (
              <div className="flex items-center gap-1 px-2 py-1 bg-forest/10 rounded-full">
                <Crown className="h-3 w-3 text-forest" />
                <span className="text-xs font-medium text-forest">Circle</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/account')}
              className="text-calm-text-secondary hover:text-forest"
            >
              Account
            </Button>
          </div>
        </div>
      </header>
      
      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* A) Welcome Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl text-calm-text mb-2" style={{fontFamily: 'DM Serif Display, serif'}}>
            {user?.name ? `Welcome back, ${user.name.split(' ')[0]}` : 'Welcome back'}
          </h1>
          <p className="text-calm-text-secondary text-sm">
            Your dashboard is quiet by default. Request a suggestion when you're ready.
          </p>
        </div>
        
        {/* B) Profile Context Block - ALWAYS VISIBLE */}
        <ProfileContextBlock 
          profileSummary={dashboardData.profile_summary}
          onEditProfile={handleEditProfile}
        />
        
        {/* C) Suggestion Area */}
        {suggestion ? (
          <ActionCard
            suggestion={suggestion}
            onAccept={handleAccept}
            onSkip={handleSkip}
            isLoading={isResponding}
          />
        ) : !hasProfile ? (
          <CreateProfileCard onCreateProfile={handleCreateProfile} />
        ) : (
          <ReadyCard
            onRequest={handleRequestSuggestion}
            isRequesting={isRequesting}
            isProfileComplete={isProfileComplete}
            error={error}
          />
        )}
        
        {/* D) Recent Action */}
        <RecentActionCard recentAction={dashboardData.recent_action} />
        
        {/* E) History */}
        <HistoryCard history={dashboardData.history} />
        
        {/* F) Circle Promo */}
        <CirclePromo 
          isCircleMember={isCircleMember}
          onLearnMore={handleCircleLearnMore}
        />
      </div>
      
      {/* Confirmation Toast */}
      <ConfirmationToast message={toast.message} show={toast.show} />
    </div>
  );
};

export default Dashboard;
